import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
} from '@mui/material';

const ScanVehiclePage = () => {
  const { registrationId } = useParams();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRegistration = async () => {
      try {
        const docRef = doc(db, 'registrations', registrationId);
        const snapshot = await getDoc(docRef);

        if (!snapshot.exists()) {
          setError('No registration found for this QR code.');
          return;
        }

        setRegistration({ id: snapshot.id, ...snapshot.data() });
      } catch (err) {
        setError('Unable to load vehicle details.');
      } finally {
        setLoading(false);
      }
    };

    fetchRegistration();
  }, [registrationId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Card sx={{ width: '100%', maxWidth: 580, p: 3, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Vehicle Not Found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button component={Link} to="/" variant="contained">
            Go to Home
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 680, p: 3, boxShadow: 6, borderRadius: 4 }}>
        <CardContent>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Vehicle Owner Details
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Scan successful. This page shows the registered owner for this vehicle.
          </Typography>

          <List sx={{ my: 2 }}>
            <ListItem disableGutters>
              <ListItemText primary="Owner Name" secondary={registration.name || '-'} />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="Vehicle Number" secondary={registration.vehicleNumber || '-'} />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="Primary Contact" secondary={registration.contact || '-'} />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="WhatsApp" secondary={registration.whatsapp || '-'} />
            </ListItem>
          </List>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Emergency Contacts
            </Typography>
            {Array.isArray(registration.emergencyContacts) && registration.emergencyContacts.length > 0 ? (
              registration.emergencyContacts.map((contact, idx) => (
                <Chip
                  key={idx}
                  label={`${contact.name} (${contact.relation}) • ${contact.number}`}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))
            ) : (
              <Typography color="text.secondary">No emergency contacts registered.</Typography>
            )}
          </Box>

          <Button
            component={Link}
            to={`/alert/${encodeURIComponent(registration.vehicleNumber)}`}
            variant="contained"
            sx={{ mr: 2 }}
          >
            Alert Vehicle
          </Button>
          <Button component={Link} to="/" variant="outlined">
            Back to Home
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ScanVehiclePage;
